import React, { useState } from 'react';
import { XIcon, ChevronDownIcon } from 'lucide-react';
import { AVAILABLE_FILTERS, ActiveFilter } from '../types/filters';
interface FilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  activeFilters: ActiveFilter[];
  onApplyFilters: (filters: ActiveFilter[]) => void;
}
export function FilterModal({
  isOpen,
  onClose,
  activeFilters,
  onApplyFilters
}: FilterModalProps) {
  const [tempFilters, setTempFilters] = useState<ActiveFilter[]>(activeFilters);
  const [expandedFilters, setExpandedFilters] = useState<Set<string>>(new Set());
  if (!isOpen) return null;
  const toggleFilterExpanded = (filterId: string) => {
    setExpandedFilters(prev => {
      const next = new Set(prev);
      if (next.has(filterId)) {
        next.delete(filterId);
      } else {
        next.add(filterId);
      }
      return next;
    });
  };
  const updateFilterValue = (filterId: string, value: any, label: string) => {
    setTempFilters(prev => {
      const existing = prev.find(f => f.filterId === filterId);
      if (existing) {
        return prev.map(f => f.filterId === filterId ? {
          ...f,
          value
        } : f);
      }
      return [...prev, {
        filterId,
        value,
        label
      }];
    });
  };
  const handleApply = () => {
    onApplyFilters(tempFilters);
    onClose();
  };
  const handleReset = () => {
    setTempFilters([]);
  };
  const leadFilters = AVAILABLE_FILTERS.filter(f => f.category === 'lead');
  const callFilters = AVAILABLE_FILTERS.filter(f => f.category === 'call');
  const otherFilters = AVAILABLE_FILTERS.filter(f => f.category === 'other');
  return <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 backdrop-blur-sm pt-20" data-id="element-1005">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-5xl max-h-[80vh] overflow-hidden flex flex-col" data-id="element-1006">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200" data-id="element-1007">
          <h2 className="text-xl font-semibold text-gray-800" data-id="element-1008">FILTERS</h2>
          <button onClick={onClose} className="p-2 text-gray-500 hover:bg-gray-100 rounded-full transition-colors" data-id="element-1009">
            <XIcon size={20} data-id="element-1010" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6" data-id="element-1011">
          <div className="grid grid-cols-3 gap-8" data-id="element-1012">
            {/* LEAD Section */}
            <div data-id="element-1013">
              <h3 className="text-sm font-semibold text-blue-600 mb-4 uppercase tracking-wider" data-id="element-1014">
                LEAD
              </h3>
              <div className="space-y-3" data-id="element-1015">
                {leadFilters.map(filter => {
                const isExpanded = expandedFilters.has(filter.id);
                const currentValue = tempFilters.find(f => f.filterId === filter.id)?.value;
                return <div key={filter.id} data-id="element-1016">
                      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-1017">
                        {filter.label}
                      </label>
                      <button onClick={() => toggleFilterExpanded(filter.id)} className="w-full flex items-center justify-between px-4 py-2 bg-white border border-gray-300 rounded-md text-sm text-gray-700 hover:border-gray-400 transition-colors" data-id="element-1018">
                        <span data-id="element-1019">{currentValue || 'Select'}</span>
                        <ChevronDownIcon size={16} className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`} data-id="element-1020" />
                      </button>
                      {isExpanded && <div className="mt-2 bg-gray-50 border border-gray-200 rounded-md p-2 space-y-1" data-id="element-1021">
                          {['Option 1', 'Option 2', 'Option 3'].map(option => <button key={option} onClick={() => {
                      updateFilterValue(filter.id, option, filter.label);
                      toggleFilterExpanded(filter.id);
                    }} className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-white rounded transition-colors" data-id="element-1022">
                                {option}
                              </button>)}
                        </div>}
                    </div>;
              })}
              </div>
            </div>

            {/* CALL Section */}
            <div data-id="element-1023">
              <h3 className="text-sm font-semibold text-blue-600 mb-4 uppercase tracking-wider" data-id="element-1024">
                CALL
              </h3>
              <div className="space-y-3" data-id="element-1025">
                {callFilters.map(filter => {
                const isExpanded = expandedFilters.has(filter.id);
                const currentValue = tempFilters.find(f => f.filterId === filter.id)?.value;
                return <div key={filter.id} data-id="element-1026">
                      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-1027">
                        {filter.label}
                      </label>
                      <button onClick={() => toggleFilterExpanded(filter.id)} className="w-full flex items-center justify-between px-4 py-2 bg-white border border-gray-300 rounded-md text-sm text-gray-700 hover:border-gray-400 transition-colors" data-id="element-1028">
                        <span data-id="element-1029">{currentValue || 'Select'}</span>
                        <ChevronDownIcon size={16} className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`} data-id="element-1030" />
                      </button>
                      {isExpanded && <div className="mt-2 bg-gray-50 border border-gray-200 rounded-md p-2 space-y-1" data-id="element-1031">
                          {['Option 1', 'Option 2', 'Option 3'].map(option => <button key={option} onClick={() => {
                      updateFilterValue(filter.id, option, filter.label);
                      toggleFilterExpanded(filter.id);
                    }} className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-white rounded transition-colors" data-id="element-1032">
                                {option}
                              </button>)}
                        </div>}
                    </div>;
              })}
              </div>
            </div>

            {/* OTHER Section */}
            <div data-id="element-1033">
              <h3 className="text-sm font-semibold text-blue-600 mb-4 uppercase tracking-wider" data-id="element-1034">
                OTHER
              </h3>
              <div className="space-y-3" data-id="element-1035">
                {otherFilters.map(filter => {
                const isExpanded = expandedFilters.has(filter.id);
                const currentValue = tempFilters.find(f => f.filterId === filter.id)?.value;
                if (filter.type === 'date') {
                  return <div key={filter.id} data-id="element-1036">
                        <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-1037">
                          {filter.label}
                        </label>
                        <input type="text" placeholder="01 Jan 2026 - 29 Jan 2026" value={currentValue || ''} onChange={e => updateFilterValue(filter.id, e.target.value, filter.label)} className="w-full px-4 py-2 bg-white border border-gray-300 rounded-md text-sm text-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-1038" />
                      </div>;
                }
                if (filter.type === 'checkbox' && filter.options) {
                  return <div key={filter.id} data-id="element-1039">
                        <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-1040">
                          {filter.label}
                        </label>
                        <div className="flex space-x-3" data-id="element-1041">
                          {filter.options.map(option => <label key={option} className="flex items-center cursor-pointer" data-id="element-1042">
                              <input type="checkbox" className="w-4 h-4 text-blue-500 rounded border-gray-300 focus:ring-2 focus:ring-blue-500" data-id="element-1043" />
                              <span className="ml-2 text-sm text-gray-700" data-id="element-1044">
                                {option}
                              </span>
                            </label>)}
                        </div>
                      </div>;
                }
                return <div key={filter.id} data-id="element-1045">
                      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-1046">
                        {filter.label}
                      </label>
                      <button onClick={() => toggleFilterExpanded(filter.id)} className="w-full flex items-center justify-between px-4 py-2 bg-white border border-gray-300 rounded-md text-sm text-gray-700 hover:border-gray-400 transition-colors" data-id="element-1047">
                        <span data-id="element-1048">{currentValue || 'Select'}</span>
                        <ChevronDownIcon size={16} className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`} data-id="element-1049" />
                      </button>
                      {isExpanded && <div className="mt-2 bg-gray-50 border border-gray-200 rounded-md p-2 space-y-1" data-id="element-1050">
                          {['Option 1', 'Option 2', 'Option 3'].map(option => <button key={option} onClick={() => {
                      updateFilterValue(filter.id, option, filter.label);
                      toggleFilterExpanded(filter.id);
                    }} className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-white rounded transition-colors" data-id="element-1051">
                                {option}
                              </button>)}
                        </div>}
                    </div>;
              })}
              </div>
            </div>
          </div>

          {/* Additional Filters Button */}
          <button className="mt-6 flex items-center text-blue-600 font-medium text-sm hover:text-blue-700 transition-colors" data-id="element-1052">
            <span className="w-5 h-5 rounded-full border-2 border-blue-600 flex items-center justify-center mr-2" data-id="element-1053">
              <span className="text-blue-600 text-lg leading-none" data-id="element-1054">+</span>
            </span>
            ADDITIONAL FILTERS
          </button>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 px-6 py-4 border-t border-gray-200 bg-gray-50" data-id="element-1055">
          <button onClick={handleReset} className="px-6 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors font-medium" data-id="element-1056">
            Reset
          </button>
          <button onClick={handleApply} className="px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors font-medium" data-id="element-1057">
            Apply
          </button>
        </div>
      </div>
    </div>;
}